import { memo } from "react";
import { Box, Button, Chip, SvgIcon } from "@mui/material";

import { useTheme } from "@mui/material/styles";
import { CellContext } from "@tanstack/react-table";
import { ClockFadingIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { IRule } from "interfaces/rules.interface";
import { RuleTypes } from "interfaces/ruleType";
import { IZone } from "interfaces/zone.interface";
import { parseTime } from "utils/rules.util";

type ITimeframeDataTime = { type: "range"; start: string; end: string };
type ITimeframeDataSchedule = { type: "cron"; hour: string; minute: string };

function getActiveTimeFrame(
  rule: IRule,
): ITimeframeDataTime | ITimeframeDataSchedule | null {
  if (rule.type === RuleTypes.RRDeviation) {
    return {
      type: "cron",
      hour: rule.schedule?.hour || "",
      minute: rule.schedule?.minute || "",
    };
  }

  if (
    rule.type === RuleTypes.DeviceOffline ||
    rule.type === "device.event" ||
    !rule.time_restriction
  ) {
    return null;
  }

  const timeRestriction = rule.time_restriction;

  return {
    type: "range",
    start: timeRestriction.start || "",
    end: timeRestriction.end || "",
  };
}

export function RuleTypeChip(cell: CellContext<IRule, IRule["type"]>) {
  const { t } = useTranslation();

  const ruleType = cell.getValue();

  return (
    <Chip
      label={ruleType === RuleTypes.DeviceEvent ? "" : t(ruleType)}
      sx={(theme) => ({
        width: "148px",
        height: "auto",
        fontSize: 14,
        paddingBottom: "4px",
        paddingTop: "4px",
        borderRadius: "2px",
        letterSpacing: "0.4px",
        border: `1px solid ${theme.vars.palette.divider}`,
      })}
    />
  );
}

export function PresetChip(cell: CellContext<IRule, unknown>) {
  const { t } = useTranslation();
  const theme = useTheme();

  const rule = cell.row.original;

  if (rule.type === RuleTypes.RRDeviation) {
    return <Box>&nbsp;</Box>;
  }

  return (
    <Chip
      label={t(
        ("conditions_preset" in rule && rule.conditions_preset) || "custom",
      )}
      sx={{
        textTransform: "capitalize",
        borderRadius: "2px",
        width: "89px",
        fontSize: 14,
        letterSpacing: "0.4px",
        paddingTop: "4px",
        paddingBottom: "4px",
        border: `1px solid ${theme.vars.palette.divider}`,
      }}
    />
  );
}

export function ActiveTimeFrameChip(cell: CellContext<IRule, unknown>) {
  const { t } = useTranslation();
  const theme = useTheme();

  const rule = cell.row.original;

  const val = getActiveTimeFrame(rule);

  if (!val) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Chip
          label={t("24_hours")}
          sx={{
            minWidth: "120px",
            background: "none",
            textTransform: "capitalize",
            borderRadius: "16px",
            width: "89px",
            fontSize: 14,
            letterSpacing: "0.4px",
            paddingTop: "4px",
            paddingBottom: "4px",
            border: "none",
          }}
        />
      </Box>
    );
  }

  if (val.type === "cron") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Chip
          label={`${val.hour}:${val.minute}`}
          icon={
            <Box sx={{ fontSize: 16, display: "flex" }}>
              <SvgIcon fontSize="inherit">
                <ClockFadingIcon />
              </SvgIcon>
            </Box>
          }
          sx={{
            minWidth: "120px",
            background: "none",
            textTransform: "capitalize",
            borderRadius: "16px",
            width: "89px",
            fontSize: 14,
            letterSpacing: "0.4px",
            paddingTop: "4px",
            paddingBottom: "4px",
            border: `1px solid ${theme.vars.palette.divider}`,
          }}
        />
      </Box>
    );
  }

  if (val.type === "range") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Chip
          label={
            val ? `${parseTime(val.start)} - ${parseTime(val.end)}` : "00 - 24"
          }
          sx={{
            minWidth: "120px",
            background: "none",
            textTransform: "capitalize",
            borderRadius: "16px",
            width: "89px",
            fontSize: 14,
            letterSpacing: "0.4px",
            paddingTop: "4px",
            paddingBottom: "4px",
            border: `1px solid ${theme.vars.palette.divider}`,
          }}
        />
      </Box>
    );
  }

  return <div />;
}

export function InheritedChip<T extends Pick<IRule, "inherited">>(
  cell: CellContext<T, T["inherited"]>,
) {
  const { t } = useTranslation();
  const theme = useTheme();

  const inherited = cell.getValue();

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Chip
        label={inherited ? t("inherited") : t("direct")}
        sx={{
          minWidth: "90px",
          background: "none",
          textTransform: "capitalize",
          borderRadius: "16px",
          width: "89px",
          fontSize: 14,
          letterSpacing: "0.4px",
          paddingTop: "4px",
          paddingBottom: "4px",
          border: `1px solid ${theme.vars.palette.divider}`,
        }}
      />
      <Box sx={{ visibility: "hidden" }} width="24px" />
    </Box>
  );
}

export const MemoizedZoneComponentComponent = memo(
  function RuleInheritedComponent({
    zone,
    selectedZone,
    hasPermission,
    onClick,
    ruleEntityType,
  }: {
    zone: IZone | undefined;
    selectedZone: string;
    hasPermission: boolean;
    onClick: (zone: IZone) => void;
    ruleEntityType: "zone" | "subject";
  }) {
    if (!zone) {
      return <Box>-</Box>;
    }
    if (zone?.id === selectedZone && ruleEntityType === "zone") {
      return <Box>-</Box>;
    }
    if (hasPermission) {
      return (
        <Button variant={"text"} onClick={() => onClick(zone)}>
          {zone?.name}
        </Button>
      );
    }
    return <Box sx={{ fontSize: 14 }}>{zone?.name || "-"}</Box>;
  },
);
